import { useEffect, useState } from "react";
import { Download, Smartphone, X } from "lucide-react";

export default function InstallPwaButton({ variant = "banner" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(() => {
    if (typeof window === "undefined") return false;
    return Boolean(
      window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone
    );
  });
  const [dismissed, setDismissed] = useState(() => {
    return localStorage.getItem("pwa_dismissed") === "1";
  });
  const [isIOS] = useState(() => {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(ua) && !window.navigator.standalone;
  });
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem("pwa_dismissed", "1");
    setDismissed(true);
  };

  if (installed) return null;

  // Show iOS instruction modal
  if (showIOSHint) {
    return (
      <div className="fixed inset-0 z-[100] bg-app/85 backdrop-blur-md flex items-end sm:items-center justify-center p-4" onClick={() => setShowIOSHint(false)}>
        <div className="bg-surface border border-brand/30 rounded-3xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setShowIOSHint(false)} className="absolute top-4 right-4 p-2 rounded-full hover:bg-elevated"><X className="w-4 h-4" /></button>
          <Smartphone className="w-10 h-10 text-brand mb-3" strokeWidth={1.4} />
          <h3 className="font-display text-xl font-semibold">Installa su iPhone</h3>
          <ol className="mt-4 space-y-3 text-sm text-secondary-fg">
            <li>1. Tocca il bottone <strong className="text-primary-fg">Condividi</strong> 􀈂 in basso (Safari)</li>
            <li>2. Scorri e tocca <strong className="text-primary-fg">&ldquo;Aggiungi a Home&rdquo;</strong></li>
            <li>3. Conferma con <strong className="text-primary-fg">&ldquo;Aggiungi&rdquo;</strong> in alto a destra</li>
            <li>4. L&apos;icona Family Brain apparirà sulla tua schermata home 🎉</li>
          </ol>
        </div>
      </div>
    );
  }

  // Compact button (for nav/settings)
  if (variant === "button") {
    if (!deferredPrompt && !isIOS) return null;
    return (
      <button
        onClick={install}
        data-testid="pwa-install-button"
        className="btn-secondary flex items-center gap-2 text-sm"
      >
        <Download className="w-4 h-4" /> Installa app
      </button>
    );
  }

  // Banner (default)
  if (dismissed) return null;
  if (!deferredPrompt && !isIOS) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-40 bg-surface border border-brand/40 rounded-2xl p-4 shadow-xl animate-fade-up flex items-start gap-3"
    >
      <div className="w-10 h-10 rounded-2xl bg-brand grid place-items-center shrink-0">
        <Smartphone className="w-5 h-5 text-white" strokeWidth={1.6} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm">Installa Family Brain</div>
        <div className="text-xs text-tertiary-fg mt-0.5">Accedi rapidamente come una vera app</div>
        <div className="flex gap-2 mt-3">
          <button onClick={install} data-testid="pwa-install-cta" className="btn-primary !py-1.5 !px-3 text-xs">
            Installa
          </button>
          <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-xs text-tertiary-fg hover:text-primary-fg px-2">
            Non ora
          </button>
        </div>
      </div>
      <button onClick={dismiss} className="p-1 rounded-full hover:bg-elevated text-tertiary-fg">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
